import { isImageGraphic } from '../symbols/model';
import { isSymbolBacking } from '../symbols/backing';
import type { FieldDefinition } from '../data/model';
import { isHexColour, normaliseHexColour } from './colour';
import {
  categorisableFields,
  categoryValueKey,
  MAX_GRADUATED_CLASS_COUNT,
  MIN_GRADUATED_CLASS_COUNT,
  numericFields,
} from './classification';
import { hasSymbolOverrides } from './effectiveSymbols';
import type {
  GeometrySymbolPatch,
  GraduatedVisualVariable,
  LineDashPreset,
  LineSymbol,
  PointShape,
  PointSymbol,
  PolygonSymbol,
  ThematicSymbolDefinition,
  VectorLayerStyle,
  VectorSymbolOverrides,
} from './model';
import { isCategoricalPaletteId, isSequentialPaletteId } from './palettes';

export const STYLE_LIMITS = {
  pointRadius: { minimum: 1, maximum: 40 },
  outlineWidth: { minimum: 0, maximum: 12 },
  lineWidth: { minimum: 0.5, maximum: 30 },
  lineOffset: { minimum: -30, maximum: 30 },
  opacity: { minimum: 0, maximum: 1 },
} as const;

export const THEMATIC_LEGEND_LABEL_MAX_LENGTH = 120;

export const POINT_SHAPES: readonly PointShape[] = ['circle', 'square', 'triangle'];
export const LINE_DASH_PRESETS: readonly LineDashPreset[] = [
  'solid',
  'dashed',
  'shortDash',
  'dotted',
  'dashDot',
];

function isFiniteInRange(value: unknown, minimum: number, maximum: number): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum
  );
}

function clampFinite(value: unknown, minimum: number, maximum: number): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(maximum, Math.max(minimum, value));
}

function hasOnlyKeys(value: object, keys: readonly string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key));
}

function pointSymbolIsValid(symbol: PointSymbol): boolean {
  return (
    (symbol.backing === undefined || isSymbolBacking(symbol.backing)) &&
    ((typeof symbol.shape === 'string' && POINT_SHAPES.includes(symbol.shape)) ||
      isImageGraphic(symbol.shape)) &&
    (symbol.rotationDegrees === undefined || isFiniteInRange(symbol.rotationDegrees, -180, 180)) &&
    (symbol.anchor === undefined || ['center', 'bottom'].includes(symbol.anchor)) &&
    (symbol.tint === undefined || symbol.tint === null || isHexColour(symbol.tint)) &&
    isHexColour(symbol.fillColor) &&
    isFiniteInRange(
      symbol.radius,
      STYLE_LIMITS.pointRadius.minimum,
      STYLE_LIMITS.pointRadius.maximum,
    ) &&
    isFiniteInRange(symbol.opacity, STYLE_LIMITS.opacity.minimum, STYLE_LIMITS.opacity.maximum) &&
    isHexColour(symbol.outlineColor) &&
    isFiniteInRange(
      symbol.outlineWidth,
      STYLE_LIMITS.outlineWidth.minimum,
      STYLE_LIMITS.outlineWidth.maximum,
    )
  );
}

function lineSymbolIsValid(symbol: LineSymbol): boolean {
  return (
    isHexColour(symbol.color) &&
    isFiniteInRange(symbol.width, STYLE_LIMITS.lineWidth.minimum, STYLE_LIMITS.lineWidth.maximum) &&
    isFiniteInRange(symbol.opacity, STYLE_LIMITS.opacity.minimum, STYLE_LIMITS.opacity.maximum) &&
    LINE_DASH_PRESETS.includes(symbol.dash) &&
    isFiniteInRange(symbol.offset, STYLE_LIMITS.lineOffset.minimum, STYLE_LIMITS.lineOffset.maximum)
  );
}

function polygonSymbolIsValid(symbol: PolygonSymbol): boolean {
  return (
    isHexColour(symbol.fillColor) &&
    isFiniteInRange(
      symbol.fillOpacity,
      STYLE_LIMITS.opacity.minimum,
      STYLE_LIMITS.opacity.maximum,
    ) &&
    isHexColour(symbol.outlineColor) &&
    isFiniteInRange(
      symbol.outlineWidth,
      STYLE_LIMITS.outlineWidth.minimum,
      STYLE_LIMITS.outlineWidth.maximum,
    )
  );
}

function pointOverrideIsValid(values: Partial<PointSymbol>): boolean {
  return (
    hasOnlyKeys(values, [
      'shape',
      'rotationDegrees',
      'anchor',
      'tint',
      'backing',
      'fillColor',
      'radius',
      'opacity',
      'outlineColor',
      'outlineWidth',
    ]) &&
    (values.shape === undefined ||
      (typeof values.shape === 'string' && POINT_SHAPES.includes(values.shape)) ||
      isImageGraphic(values.shape)) &&
    (values.rotationDegrees === undefined || isFiniteInRange(values.rotationDegrees, -180, 180)) &&
    (values.anchor === undefined || ['center', 'bottom'].includes(values.anchor)) &&
    (values.tint === undefined || values.tint === null || isHexColour(values.tint)) &&
    (values.backing === undefined || isSymbolBacking(values.backing)) &&
    (values.fillColor === undefined || isHexColour(values.fillColor)) &&
    (values.radius === undefined ||
      isFiniteInRange(
        values.radius,
        STYLE_LIMITS.pointRadius.minimum,
        STYLE_LIMITS.pointRadius.maximum,
      )) &&
    (values.opacity === undefined ||
      isFiniteInRange(
        values.opacity,
        STYLE_LIMITS.opacity.minimum,
        STYLE_LIMITS.opacity.maximum,
      )) &&
    (values.outlineColor === undefined || isHexColour(values.outlineColor)) &&
    (values.outlineWidth === undefined ||
      isFiniteInRange(
        values.outlineWidth,
        STYLE_LIMITS.outlineWidth.minimum,
        STYLE_LIMITS.outlineWidth.maximum,
      ))
  );
}

function lineOverrideIsValid(values: Partial<LineSymbol>): boolean {
  return (
    hasOnlyKeys(values, ['color', 'width', 'opacity', 'dash', 'offset']) &&
    (values.color === undefined || isHexColour(values.color)) &&
    (values.width === undefined ||
      isFiniteInRange(
        values.width,
        STYLE_LIMITS.lineWidth.minimum,
        STYLE_LIMITS.lineWidth.maximum,
      )) &&
    (values.opacity === undefined ||
      isFiniteInRange(
        values.opacity,
        STYLE_LIMITS.opacity.minimum,
        STYLE_LIMITS.opacity.maximum,
      )) &&
    (values.dash === undefined || LINE_DASH_PRESETS.includes(values.dash)) &&
    (values.offset === undefined ||
      isFiniteInRange(
        values.offset,
        STYLE_LIMITS.lineOffset.minimum,
        STYLE_LIMITS.lineOffset.maximum,
      ))
  );
}

function polygonOverrideIsValid(values: Partial<PolygonSymbol>): boolean {
  return (
    hasOnlyKeys(values, ['fillColor', 'fillOpacity', 'outlineColor', 'outlineWidth']) &&
    (values.fillColor === undefined || isHexColour(values.fillColor)) &&
    (values.fillOpacity === undefined ||
      isFiniteInRange(
        values.fillOpacity,
        STYLE_LIMITS.opacity.minimum,
        STYLE_LIMITS.opacity.maximum,
      )) &&
    (values.outlineColor === undefined || isHexColour(values.outlineColor)) &&
    (values.outlineWidth === undefined ||
      isFiniteInRange(
        values.outlineWidth,
        STYLE_LIMITS.outlineWidth.minimum,
        STYLE_LIMITS.outlineWidth.maximum,
      ))
  );
}

export function symbolOverridesAreValid(overrides: VectorSymbolOverrides | undefined): boolean {
  if (!overrides) return true;
  return (
    hasOnlyKeys(overrides, ['point', 'line', 'polygon']) &&
    (overrides.point === undefined || pointOverrideIsValid(overrides.point)) &&
    (overrides.line === undefined || lineOverrideIsValid(overrides.line)) &&
    (overrides.polygon === undefined || polygonOverrideIsValid(overrides.polygon))
  );
}

function thematicSymbolIsValid(symbol: ThematicSymbolDefinition): boolean {
  return (
    hasOnlyKeys(symbol, ['generated', 'override', 'legendLabel']) &&
    hasOnlyKeys(symbol.generated, ['colour', 'pointRadius', 'lineWidth']) &&
    (symbol.generated.colour === undefined || isHexColour(symbol.generated.colour)) &&
    (symbol.generated.pointRadius === undefined ||
      isFiniteInRange(
        symbol.generated.pointRadius,
        STYLE_LIMITS.pointRadius.minimum,
        STYLE_LIMITS.pointRadius.maximum,
      )) &&
    (symbol.generated.lineWidth === undefined ||
      isFiniteInRange(
        symbol.generated.lineWidth,
        STYLE_LIMITS.lineWidth.minimum,
        STYLE_LIMITS.lineWidth.maximum,
      )) &&
    symbolOverridesAreValid(symbol.override) &&
    (symbol.legendLabel === undefined ||
      (typeof symbol.legendLabel === 'string' &&
        symbol.legendLabel.length > 0 &&
        symbol.legendLabel.length <= THEMATIC_LEGEND_LABEL_MAX_LENGTH &&
        symbol.legendLabel === symbol.legendLabel.trim()))
  );
}

function visualVariableIsValid(visualVariable: GraduatedVisualVariable): boolean {
  if (visualVariable.kind === 'colour') return hasOnlyKeys(visualVariable, ['kind']);
  const limits =
    visualVariable.kind === 'point-size' ? STYLE_LIMITS.pointRadius : STYLE_LIMITS.lineWidth;
  return (
    hasOnlyKeys(visualVariable, ['kind', 'includeColour', 'minimum', 'maximum']) &&
    typeof visualVariable.includeColour === 'boolean' &&
    isFiniteInRange(visualVariable.minimum, limits.minimum, limits.maximum) &&
    isFiniteInRange(visualVariable.maximum, limits.minimum, limits.maximum) &&
    visualVariable.minimum <= visualVariable.maximum
  );
}

function classificationIsValid(
  style: VectorLayerStyle,
  fields: readonly FieldDefinition[],
): boolean {
  const classification = style.classification;
  if (classification.mode === 'single') return true;
  if (classification.mode === 'categorised') {
    if (
      !categorisableFields(fields).some((field) => field.name === classification.field) ||
      !isCategoricalPaletteId(classification.paletteId) ||
      classification.categories.length === 0 ||
      !thematicSymbolIsValid(classification.other)
    ) {
      return false;
    }
    const keys = classification.categories.map((category) => categoryValueKey(category.value));
    return (
      new Set(keys).size === keys.length &&
      classification.categories.every(
        (category) =>
          thematicSymbolIsValid(category.symbol) &&
          (category.value.kind !== 'number' || Number.isFinite(category.value.value)),
      )
    );
  }

  if (
    !numericFields(fields).some((field) => field.name === classification.field) ||
    !['equal-interval', 'quantile', 'manual'].includes(classification.method) ||
    !['equal-interval', 'quantile'].includes(classification.resetMethod) ||
    !Number.isInteger(classification.classCount) ||
    classification.classCount < MIN_GRADUATED_CLASS_COUNT ||
    classification.classCount > MAX_GRADUATED_CLASS_COUNT ||
    !isSequentialPaletteId(classification.paletteId) ||
    typeof classification.paletteReversed !== 'boolean' ||
    !visualVariableIsValid(classification.visualVariable) ||
    classification.classes.length === 0 ||
    classification.classes.length > classification.classCount ||
    classification.boundaries.length !== classification.classes.length + 1 ||
    !thematicSymbolIsValid(classification.nullSymbol)
  ) {
    return false;
  }

  const boundariesAreValid = classification.boundaries.every((boundary, index, boundaries) => {
    const previous = boundaries[index - 1];
    const constantOnly = boundaries.length === 2 && boundary === previous;
    return (
      Number.isFinite(boundary) &&
      (previous === undefined || boundary > previous || (index === 1 && constantOnly))
    );
  });
  return (
    boundariesAreValid &&
    classification.classes.every((entry) => thematicSymbolIsValid(entry.symbol))
  );
}

export function isVectorLayerStyleValid(
  style: VectorLayerStyle,
  fields: readonly FieldDefinition[],
): boolean {
  return (
    style.kind === 'vector' &&
    pointSymbolIsValid(style.symbols.point) &&
    lineSymbolIsValid(style.symbols.line) &&
    polygonSymbolIsValid(style.symbols.polygon) &&
    classificationIsValid(style, fields)
  );
}

function normalisedColour(value: unknown): string | null {
  return typeof value === 'string' ? normaliseHexColour(value) : null;
}

export function normaliseGeometrySymbolPatch(
  patch: GeometrySymbolPatch,
): GeometrySymbolPatch | null {
  if (patch.geometry === 'point') {
    const values = patch.values;
    const fillColor =
      values.fillColor === undefined ? undefined : normalisedColour(values.fillColor);
    const outlineColor =
      values.outlineColor === undefined ? undefined : normalisedColour(values.outlineColor);
    const radius =
      values.radius === undefined
        ? undefined
        : clampFinite(
            values.radius,
            STYLE_LIMITS.pointRadius.minimum,
            STYLE_LIMITS.pointRadius.maximum,
          );
    const opacity =
      values.opacity === undefined
        ? undefined
        : clampFinite(values.opacity, STYLE_LIMITS.opacity.minimum, STYLE_LIMITS.opacity.maximum);
    const outlineWidth =
      values.outlineWidth === undefined
        ? undefined
        : clampFinite(
            values.outlineWidth,
            STYLE_LIMITS.outlineWidth.minimum,
            STYLE_LIMITS.outlineWidth.maximum,
          );
    if (
      (values.tint !== undefined && values.tint !== null && !isHexColour(values.tint)) ||
      (values.backing !== undefined && !isSymbolBacking(values.backing)) ||
      fillColor === null ||
      outlineColor === null ||
      radius === null ||
      opacity === null ||
      outlineWidth === null ||
      (values.shape !== undefined &&
        !(typeof values.shape === 'string' && POINT_SHAPES.includes(values.shape)) &&
        !isImageGraphic(values.shape)) ||
      (values.rotationDegrees !== undefined &&
        !isFiniteInRange(values.rotationDegrees, -180, 180)) ||
      (values.anchor !== undefined && !['center', 'bottom'].includes(values.anchor))
    ) {
      return null;
    }
    return {
      geometry: 'point',
      values: {
        ...(values.shape === undefined ? {} : { shape: values.shape }),
        ...(values.rotationDegrees === undefined
          ? {}
          : { rotationDegrees: values.rotationDegrees }),
        ...(values.anchor === undefined ? {} : { anchor: values.anchor }),
        ...(values.tint === undefined ? {} : { tint: values.tint }),
        ...(values.backing === undefined ? {} : { backing: { ...values.backing } }),
        ...(fillColor === undefined ? {} : { fillColor }),
        ...(outlineColor === undefined ? {} : { outlineColor }),
        ...(radius === undefined ? {} : { radius }),
        ...(opacity === undefined ? {} : { opacity }),
        ...(outlineWidth === undefined ? {} : { outlineWidth }),
      },
    };
  }

  if (patch.geometry === 'line') {
    const values = patch.values;
    const color = values.color === undefined ? undefined : normalisedColour(values.color);
    const width =
      values.width === undefined
        ? undefined
        : clampFinite(values.width, STYLE_LIMITS.lineWidth.minimum, STYLE_LIMITS.lineWidth.maximum);
    const opacity =
      values.opacity === undefined
        ? undefined
        : clampFinite(values.opacity, STYLE_LIMITS.opacity.minimum, STYLE_LIMITS.opacity.maximum);
    const offset =
      values.offset === undefined
        ? undefined
        : clampFinite(
            values.offset,
            STYLE_LIMITS.lineOffset.minimum,
            STYLE_LIMITS.lineOffset.maximum,
          );
    if (
      color === null ||
      width === null ||
      opacity === null ||
      offset === null ||
      (values.dash !== undefined && !LINE_DASH_PRESETS.includes(values.dash))
    ) {
      return null;
    }
    return {
      geometry: 'line',
      values: {
        ...(color === undefined ? {} : { color }),
        ...(width === undefined ? {} : { width }),
        ...(opacity === undefined ? {} : { opacity }),
        ...(values.dash === undefined ? {} : { dash: values.dash }),
        ...(offset === undefined ? {} : { offset }),
      },
    };
  }

  const values = patch.values;
  const fillColor = values.fillColor === undefined ? undefined : normalisedColour(values.fillColor);
  const outlineColor =
    values.outlineColor === undefined ? undefined : normalisedColour(values.outlineColor);
  const fillOpacity =
    values.fillOpacity === undefined
      ? undefined
      : clampFinite(values.fillOpacity, STYLE_LIMITS.opacity.minimum, STYLE_LIMITS.opacity.maximum);
  const outlineWidth =
    values.outlineWidth === undefined
      ? undefined
      : clampFinite(
          values.outlineWidth,
          STYLE_LIMITS.outlineWidth.minimum,
          STYLE_LIMITS.outlineWidth.maximum,
        );
  if (
    fillColor === null ||
    outlineColor === null ||
    fillOpacity === null ||
    outlineWidth === null
  ) {
    return null;
  }
  return {
    geometry: 'polygon',
    values: {
      ...(fillColor === undefined ? {} : { fillColor }),
      ...(outlineColor === undefined ? {} : { outlineColor }),
      ...(fillOpacity === undefined ? {} : { fillOpacity }),
      ...(outlineWidth === undefined ? {} : { outlineWidth }),
    },
  };
}

export function updateVectorSymbol(
  style: VectorLayerStyle,
  patch: GeometrySymbolPatch,
): VectorLayerStyle | null {
  const normalised = normaliseGeometrySymbolPatch(patch);
  if (!normalised) return null;
  if (normalised.geometry === 'point') {
    return {
      ...style,
      symbols: {
        ...style.symbols,
        point: { ...style.symbols.point, ...normalised.values },
      },
    };
  }
  if (normalised.geometry === 'line') {
    return {
      ...style,
      symbols: {
        ...style.symbols,
        line: { ...style.symbols.line, ...normalised.values },
      },
    };
  }
  return {
    ...style,
    symbols: {
      ...style.symbols,
      polygon: { ...style.symbols.polygon, ...normalised.values },
    },
  };
}

export function updateThematicSymbolOverride(
  symbol: ThematicSymbolDefinition,
  patch: GeometrySymbolPatch,
): ThematicSymbolDefinition | null {
  const normalised = normaliseGeometrySymbolPatch(patch);
  if (!normalised) return null;
  const override = symbol.override ?? {};
  const nextOverride: VectorSymbolOverrides =
    normalised.geometry === 'point'
      ? { ...override, point: { ...override.point, ...normalised.values } }
      : normalised.geometry === 'line'
        ? { ...override, line: { ...override.line, ...normalised.values } }
        : { ...override, polygon: { ...override.polygon, ...normalised.values } };
  return { ...symbol, override: nextOverride };
}

export function resetThematicSymbolOverride(
  symbol: ThematicSymbolDefinition,
  geometry?: GeometrySymbolPatch['geometry'],
): ThematicSymbolDefinition {
  if (!geometry) return { generated: symbol.generated };
  const override = { ...symbol.override };
  delete override[geometry];
  return hasSymbolOverrides(override) ? { ...symbol, override } : { generated: symbol.generated };
}
